import { z } from 'zod'
import { streamFieldLabels, streamFieldValue, type StreamFieldRow } from './stream-columns'

/**
 * Filters on the analytics build's own columns (CLI-1009): one per column the workspace has
 * chosen, in the filter bar Library and Analytics share. A column is filtered the way its values
 * read (`inferStreamColumns`): labels — one or several per row, or yes/no — by picking values; a
 * figure by a range; free text by what it contains. Keyed by the column's key, so a saved view
 * keeps its filter for the day the column comes back.
 */

/** Labels to keep — a category's, a multi's (a row with any of them), a yes/no's "Yes"/"No". */
const valuesFilterSchema = z.strictObject({ values: z.array(z.string().max(200)).max(200) })
/** Text a free-text column's value contains, ignoring case. */
const containsFilterSchema = z.strictObject({ contains: z.string().max(200) })
/** A figure's bounds, both inclusive; either may stand alone. */
const rangeFilterSchema = z.strictObject({
  min: z.number().optional(),
  max: z.number().optional(),
})

export const streamFieldFilterSchema = z.union([
  valuesFilterSchema,
  containsFilterSchema,
  rangeFilterSchema,
])
export type StreamFieldFilter = z.infer<typeof streamFieldFilterSchema>

export const streamFieldFiltersSchema = z.record(
  z.string().min(1).max(120),
  streamFieldFilterSchema,
)
export type StreamFieldFilters = z.infer<typeof streamFieldFiltersSchema>

/** Whether a filter narrows anything: values picked, text typed, or a bound set. */
export function fieldFilterActive(filter: StreamFieldFilter | undefined): boolean {
  if (!filter) return false
  if ('values' in filter) return filter.values.length > 0
  if ('contains' in filter) return filter.contains.trim() !== ''
  return filter.min !== undefined || filter.max !== undefined
}

/** Whether a row passes one column's filter. A row with no value for the column passes none that
 *  narrows. */
export function passesFieldFilter(
  hit: StreamFieldRow,
  key: string,
  filter: StreamFieldFilter,
): boolean {
  if (!fieldFilterActive(filter)) return true
  const value = streamFieldValue(hit, key)
  if ('values' in filter) {
    const labels = new Set(streamFieldLabels(value))
    return filter.values.some((label) => labels.has(label))
  }
  if ('contains' in filter) {
    const needle = filter.contains.trim().toLowerCase()
    return streamFieldLabels(value).some((label) => label.toLowerCase().includes(needle))
  }
  if (typeof value !== 'number') return false
  return (
    (filter.min === undefined || value >= filter.min) &&
    (filter.max === undefined || value <= filter.max)
  )
}

/** Whether a row passes every column filter. */
export function passesFieldFilters(
  hit: StreamFieldRow,
  filters: StreamFieldFilters | undefined,
): boolean {
  if (!filters) return true
  return Object.entries(filters).every(([key, filter]) => passesFieldFilter(hit, key, filter))
}

/**
 * The filters with only the workspace's chosen columns kept, and none that narrows nothing: a
 * column nobody chose has no filter on either page, so one carried in a saved view or from the
 * other page is set aside until the column is chosen again. Undefined when nothing is left, so a
 * selection without column filters reads as one that never had any.
 */
export function chosenFieldFilters(
  filters: StreamFieldFilters | undefined,
  chosen: readonly string[],
): StreamFieldFilters | undefined {
  if (!filters) return undefined
  const kept = Object.entries(filters).filter(
    ([key, filter]) => chosen.includes(key) && fieldFilterActive(filter),
  )
  return kept.length > 0 ? Object.fromEntries(kept) : undefined
}
